import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { AvatarComponent, EntityListComponent, UnavailableComponent } from '@shared/components';
import { MediaSrcPipe } from '@shared/pipes';
import { OrganisationFacade } from '@app/store/organisation';
import { DeleteButtonComponent } from './delete-button/delete-button.component';

@Component({
    selector: 'app-organisation-view-modal',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.css'],
    imports: [
        AsyncPipe,
        RouterLink,
        TranslateModule,
        ButtonModule,
        AvatarComponent,
        EntityListComponent,
        DeleteButtonComponent,
        UnavailableComponent,
        MediaSrcPipe,
    ],
})
export class ViewComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly organisationFacade = inject(OrganisationFacade);

    readonly organisation = this.organisationFacade.organisation;
    readonly loading = this.organisationFacade.loading;
    readonly error = this.organisationFacade.error;
    readonly saving = this.organisationFacade.saving;

    /** Prefilled "add user" modal params creating the organisation admin account for the main contact. */
    readonly createAdminParams = computed(() => {
        const organisation = this.organisation();
        if (!organisation) {
            return null;
        }

        const [firstName, ...lastName] = organisation.contactName.trim().split(/\s+/);
        return {
            role: 'organisation_admin',
            organisationId: organisation.id,
            firstName,
            lastName: lastName.join(' '),
            email: organisation.contactEmail,
        };
    });

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.organisationFacade.getOrganisation(id);
        }

        effect(() => {
            if (this.organisationFacade.saved()) {
                this.close({ replaceUrl: !this.organisationFacade.organisation() });
            }
        });
    }

    ngOnDestroy(): void {
        this.organisationFacade.clear();
    }

    onEdit(): void {
        const organisation = this.organisation();
        if (organisation) {
            this.router.navigate([{ outlets: { modal: ['organisations', 'edit', organisation.id] } }]);
        }
    }

    close({ replaceUrl } = { replaceUrl: false }): void {
        this.router.navigate([{ outlets: { modal: null } }], { replaceUrl });
    }
}
